import * as React from "react";

import StudentChoiceCasesList from "./StudentChoiceCasesList";

import { useStudentChoiceCourseSelection } from "./useStudentChoiceCourseSelection";

type TStudentChoiceContentForLowTimeModalProps = {
  newCourseBuilder2Enabled: boolean;
  documentSetListForStudentChoiceCases: TDocumentSetListForStudentChoiceCases;
  onAcknowledgeLowTime?: () => void;
  onResetCase?: () => void;
  documentId?: number;
  resetCaseUrl: string;
  userId: number;
  programName: string;
  timeOnCase: string;
};

const StudentChoiceContentForLowTimeModal = ({
  newCourseBuilder2Enabled,
  documentSetListForStudentChoiceCases,
  onAcknowledgeLowTime,
  onResetCase,
  documentId,
  resetCaseUrl,
  userId,
  programName,
  timeOnCase,
}: TStudentChoiceContentForLowTimeModalProps) => {
  const [selectedAction, setSelectedAction] = React.useState<string | null>(
    null
  );

  const {
    loading: isSubmitting,
    handleCheckboxChange,
    submitSelectedCourses,
  } = useStudentChoiceCourseSelection(documentId);

  const resetButtonRef = React.useRef<HTMLButtonElement>(null);

  const handleConfirm = async () => {
    await submitSelectedCourses();
    if (selectedAction === "acknowledgeLowTime") {
      if (onAcknowledgeLowTime) onAcknowledgeLowTime();
    } else if (selectedAction === "resetCase") {
      // Trigger Stimulus reset-case->reset action
      if (resetButtonRef.current) {
        resetButtonRef.current.click();
      }
      if (onResetCase) onResetCase();
    }
  };

  if (!newCourseBuilder2Enabled) {
    return null;
  }

  return (
    <>
      <div className="low-time-modal--student-choice-content--container">
        <p className="low-time-modal--student-choice-content--title">
          REQUIRED: Choose an action
        </p>
        <div>
          <input
            type="radio"
            id="acknowledgeLowTime"
            name="action"
            value="acknowledgeLowTime"
            checked={selectedAction === "acknowledgeLowTime"}
            onChange={() => setSelectedAction("acknowledgeLowTime")}
          />
          <label
            htmlFor="acknowledgeLowTime"
            className="low-time-modal--student-choice-content--required-action-label"
          >
            Acknowledge low time and keep your current completion.
          </label>
        </div>
        <div>
          <input
            type="radio"
            id="resetCase"
            name="action"
            value="resetCase"
            checked={selectedAction === "resetCase"}
            onChange={() => setSelectedAction("resetCase")}
          />
          <label
            htmlFor="resetCase"
            className="low-time-modal--student-choice-content--required-action-label"
          >
            Reset case to restart my time
          </label>
        </div>
      </div>
      {documentSetListForStudentChoiceCases.length > 0 && (
        <>
          <hr className="low-time-modal--student-choice-content--separator" />
          <div className="low-time-modal--student-choice-list--main-container">
            <StudentChoiceCasesList
              documentSetListForStudentChoiceCases={
                documentSetListForStudentChoiceCases
              }
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
        </>
      )}
      <div
        className="low-time-modal--student-choice-content--cta-button-container"
        data-controller="reset-case"
      >
        <button
          className="ref-btn"
          disabled={!selectedAction || isSubmitting}
          onClick={handleConfirm}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleConfirm();
            }
          }}
        >
          Confirm
        </button>
        {/* Hidden button to trigger Stimulus reset-case->reset action */}
        <button
          type="button"
          ref={resetButtonRef}
          style={{ display: "none" }}
          data-reset-case-target="resetCaseButton"
          data-action="click->reset-case#reset"
          data-reset-url={resetCaseUrl}
          data-user-id={userId}
          data-program={programName}
          data-time-on-case={timeOnCase}
          aria-label="reset case"
        />
      </div>
    </>
  );
};

export default React.memo(StudentChoiceContentForLowTimeModal);
